import { parseJsonOrNull } from "replo-utils/lib/json";
import { ReploError } from "schemas/errors";
import { z } from "zod";

/**
 * Geometry is fractional so frames can be resized without reauthoring layers.
 * Image references are durable handles; colors and fonts are literal values.
 * Fabric is a derived projection of the saved document.
 */

/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */

/** `[x, y, w, h]` as fractions of the frame, so a layer can be re-pinned into another format. */
export const canvasBoxSchema = z
  .array(z.number())
  .length(4)
  .describe("CanvasBox");

/** Which frame edge a layer holds its distance from when the frame is resized. */
export const canvasPinSchema = z
  .object({
    x: z.enum(["left", "center", "right"]),
    y: z.enum(["top", "middle", "bottom"]),
  })
  .describe("CanvasPin");

// No `undefined`: the agent's tool registry turns these schemas into JSON
// Schema, which cannot express it; producers serialize to JSON first.
const jsonPrimitiveSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
const jsonLeafSchema = z.union([
  jsonPrimitiveSchema,
  z.array(jsonPrimitiveSchema),
  z.record(z.string(), jsonPrimitiveSchema),
]);
/**
 * A serialized Fabric value: primitives nested at most two levels deep, which
 * is as deep as a gradient goes (`colorStops[].color`). Bounded rather than
 * `unknown` so a set stays structured-clone serializable across the
 * coordinator's RPC boundary.
 */
const fabricValueSchema = z.union([
  jsonLeafSchema,
  z.array(jsonLeafSchema),
  z.record(z.string(), jsonLeafSchema),
]);

/** A hex literal, or a serialized Fabric gradient. */
export const canvasFillSchema = z
  .union([z.string(), z.record(z.string(), fabricValueSchema)])
  .describe("CanvasFill");

/**
 * The visible window into an image, as fractions of the source's natural size,
 * so a crop survives the source being re-minted at another resolution.
 */
export const canvasCropSchema = z
  .object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() })
  .describe("CanvasCrop");

/**
 * A durable reference to image bytes. Never a URL for platform content: Files
 * URLs expire, so a resolver mints a fresh signed URL on load.
 */
export const canvasImageRefSchema = z
  .discriminatedUnion("source", [
    z.object({ source: z.literal("asset"), id: z.string().min(1) }),
    z.object({ source: z.literal("file"), id: z.string().min(1) }),
    z.object({
      source: z.literal("product"),
      productId: z.string().min(1),
      imageIndex: z.number().int().nonnegative(),
    }),
    // Seeded templates: bytes addressed by a plain URL.
    z.object({ source: z.literal("url"), url: z.string().min(1) }),
  ])
  .describe("CanvasImageRef");

/** Per-character overrides keyed the way Fabric keys them: `styles[lineIndex][charIndex]`. */
export const canvasTextStylesSchema = z
  .record(
    z.string(),
    z.record(z.string(), z.record(z.string(), jsonPrimitiveSchema)),
  )
  .describe("CanvasTextStyles");

/**
 * How a text layer meets its frame: `shrink` bisects the size down until the
 * copy fits, `wrap` grows the frame to the copy, `none` leaves both alone.
 */
export const canvasTextFitSchema = z
  .enum(["shrink", "wrap", "none"])
  .describe("CanvasTextFit");

/**
 * `value` is the size actually rendered; `max` and `min` bound the shrink-to-fit
 * bisect and `fit` picks the strategy. Sizes are px in frame units, rescaled on
 * a format change.
 */
export const canvasTextSizeSchema = z
  .object({
    value: z.number(),
    max: z.number().optional(),
    min: z.number().optional(),
    fit: canvasTextFitSchema.optional(),
  })
  .describe("CanvasTextSize");

/** Per-image color adjustments. */
export const canvasAdjustSchema = z
  .object({
    brightness: z.number().optional(),
    contrast: z.number().optional(),
    saturation: z.number().optional(),
    blur: z.number().optional(),
  })
  .describe("CanvasAdjust");

/* -------------------------------------------------------------------------- */
/* Layers                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * `photo` is a full-bleed bitmap and `image` a placed one; `logo` and `svg` are
 * marks. The artboard fill is `frame.background`, not a layer, so it can never
 * be selected or deleted.
 */
export const canvasLayerKindSchema = z
  .enum(["photo", "image", "text", "shape", "group", "logo", "svg"])
  .describe("CanvasLayerKind");

/** The primitive a `shape` layer draws. */
export const canvasShapeSchema = z
  .enum(["rect", "ellipse", "line"])
  .describe("CanvasShape");

/** Fabric normalizes relative, arc and shorthand commands to these absolute commands. */
export const canvasPathCommandSchema = z
  .union([
    z.tuple([z.literal("M"), z.number().finite(), z.number().finite()]),
    z.tuple([z.literal("L"), z.number().finite(), z.number().finite()]),
    z.tuple([
      z.literal("Q"),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
    ]),
    z.tuple([
      z.literal("C"),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
    ]),
    z.tuple([z.literal("Z")]),
  ])
  .describe("CanvasPathCommand");

export const canvasVectorGeometrySchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("path"),
      commands: z
        .array(canvasPathCommandSchema)
        .min(1)
        .max(10000)
        .refine(
          (commands) => commands[0]?.[0] === "M",
          "A path must begin with a move command",
        ),
    }),
    z.object({
      type: z.literal("polygon"),
      points: z
        .array(z.object({ x: z.number().finite(), y: z.number().finite() }))
        .min(3)
        .max(10000),
    }),
  ])
  .describe("CanvasVectorGeometry");

/** Intrinsic path coordinates are fitted to the text layer's box; offset is a fraction of path length. */
export const canvasTextPathSchema = z
  .object({
    commands: z
      .array(canvasPathCommandSchema)
      .min(2)
      .max(10000)
      .refine(
        (commands) => commands[0]?.[0] === "M",
        "A text path must begin with a move command",
      ),
    startOffset: z.number().finite().min(-1).max(1).optional(),
    side: z.enum(["left", "right"]).optional(),
    align: z.enum(["baseline", "center", "ascender", "descender"]).optional(),
  })
  .describe("CanvasTextPath");

/** Where text sits inside its box. */
export const canvasTextAlignSchema = z
  .enum(["left", "center", "right", "justify"])
  .describe("CanvasTextAlign");

/** Declared as an interface so the `children` self-reference type-checks; the Zod schema below matches it field for field. */
export interface CanvasLayer {
  id: string;
  kind: z.infer<typeof canvasLayerKindSchema>;
  name?: string;
  /** The template role this layer plays (`headline`, `photo`, `cta`, …). */
  slot?: string;
  box: number[];
  pin?: z.infer<typeof canvasPinSchema>;
  rotation?: number;
  skewX?: number;
  /** Intrinsic dimensions preserve rotated children during nonuniform group resizing. */
  groupSize?: [number, number];
  opacity?: number;
  locked?: boolean;
  hidden?: boolean;
  ref?: z.infer<typeof canvasImageRefSchema>;
  /** How a bitmap meets its box: `cover` crops to fill, `contain` letterboxes. */
  fit?: "cover" | "contain";
  crop?: z.infer<typeof canvasCropSchema>;
  radius?: number;
  flipX?: boolean;
  flipY?: boolean;
  shadow?: boolean;
  adjust?: z.infer<typeof canvasAdjustSchema>;
  text?: string;
  textPath?: z.infer<typeof canvasTextPathSchema>;
  font?: string;
  weight?: number;
  size?: z.infer<typeof canvasTextSizeSchema>;
  fill?: z.infer<typeof canvasFillSchema>;
  align?: z.infer<typeof canvasTextAlignSchema>;
  lineHeight?: number;
  tracking?: number;
  italic?: boolean;
  underline?: boolean;
  /** A wrapping layer is a Fabric `Textbox`; without it, a single-line `Text`. */
  wrap?: boolean;
  /** Height of the text frame, as a fraction of the frame's height. */
  frameH?: number;
  /** The pill a text layer sits on, when the legibility effect is on. */
  textBackgroundColor?: string;
  styles?: z.infer<typeof canvasTextStylesSchema>;
  shape?: z.infer<typeof canvasShapeSchema>;
  geometry?: z.infer<typeof canvasVectorGeometrySchema>;
  strokeLineCap?: "butt" | "round" | "square";
  strokeLineJoin?: "miter" | "round" | "bevel";
  fillRule?: "nonzero" | "evenodd";
  stroke?: string;
  strokeWidth?: number;
  children?: CanvasLayer[];
}

export const canvasLayerSchema: z.ZodType<CanvasLayer> = z
  .object({
    id: z.string().min(1),
    kind: canvasLayerKindSchema,
    name: z.string().optional(),
    slot: z.string().min(1).optional(),
    box: canvasBoxSchema,
    pin: canvasPinSchema.optional(),
    rotation: z.number().optional(),
    skewX: z.number().finite().optional(),
    groupSize: z
      .tuple([z.number().finite().positive(), z.number().finite().positive()])
      .optional(),
    opacity: z.number().optional(),
    locked: z.boolean().optional(),
    hidden: z.boolean().optional(),

    ref: canvasImageRefSchema.optional(),
    fit: z.enum(["cover", "contain"]).optional(),
    crop: canvasCropSchema.optional(),
    radius: z.number().optional(),
    flipX: z.boolean().optional(),
    flipY: z.boolean().optional(),
    shadow: z.boolean().optional(),
    adjust: canvasAdjustSchema.optional(),

    text: z.string().optional(),
    textPath: canvasTextPathSchema.optional(),
    font: z.string().optional(),
    weight: z.number().optional(),
    size: canvasTextSizeSchema.optional(),
    fill: canvasFillSchema.optional(),
    align: canvasTextAlignSchema.optional(),
    lineHeight: z.number().optional(),
    tracking: z.number().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    wrap: z.boolean().optional(),
    frameH: z.number().optional(),
    textBackgroundColor: z.string().optional(),
    styles: canvasTextStylesSchema.optional(),

    shape: canvasShapeSchema.optional(),
    geometry: canvasVectorGeometrySchema.optional(),
    strokeLineCap: z.enum(["butt", "round", "square"]).optional(),
    strokeLineJoin: z.enum(["miter", "round", "bevel"]).optional(),
    fillRule: z.enum(["nonzero", "evenodd"]).optional(),
    stroke: z.string().optional(),
    strokeWidth: z.number().optional(),

    // A group's children carry boxes that are fractions of the group. The
    // getter defers the self-reference until the schema above is bound.
    get children() {
      return z.array(canvasLayerSchema).optional();
    },
  })
  .refine((layer) => {
    return (
      !layer.geometry || (layer.kind === "shape" && layer.shape === undefined)
    );
  }, "Vector geometry belongs to a shape without a legacy primitive")
  .refine(
    (layer) => !layer.groupSize || layer.kind === "group",
    "Intrinsic group size belongs to a group",
  )
  .refine(
    (layer) => !layer.textPath || (layer.kind === "text" && !layer.wrap),
    "A text path belongs to an unwrapped text layer",
  )
  .describe("CanvasLayer");

/* -------------------------------------------------------------------------- */
/* Frames                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Pixel dimensions plus, for print sizes, the DPI the catalog listed — which is
 * what makes an A4 export 2480×3508 rather than a 2× bitmap.
 */
export const canvasFormatSchema = z
  .object({
    w: z.number().int().positive(),
    h: z.number().int().positive(),
    dpi: z.number().int().positive().optional(),
    /** The catalog label ("Instagram story / reel"), so a frame can name its size. */
    label: z.string().optional(),
  })
  .describe("CanvasFormat");

/**
 * Inset of the safe zone from each edge, as a fraction of the frame: a story
 * frame reserves 14% at the top and 35% at the bottom for platform chrome.
 * `null` means the whole artboard is safe.
 */
export const canvasSafeSchema = z
  .object({
    top: z.number(),
    bottom: z.number(),
    left: z.number(),
    right: z.number(),
  })
  .describe("CanvasSafe");

/** Where a derived frame came from, so its caption can show the source. */
export const canvasDerivedFromSchema = z
  .object({ frameId: z.string(), name: z.string() })
  .describe("CanvasDerivedFrom");

export const canvasFrameGenerationSchema = z
  .object({
    id: z.uuid(),
    startedAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    status: z.enum([
      "preparing",
      "generating",
      "assembling",
      "completed",
      "failed",
    ]),
    jobId: z.uuid().optional(),
    error: z.string().max(1000).optional(),
  })
  .describe("CanvasFrameGeneration");

export const canvasFrameSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    /**
     * The grid row this frame sits in and its position along it. Both are
     * fields rather than array positions because a save carries only the frames
     * that changed and leaves the rest where the file has them, so array order
     * cannot travel. The editor keeps the array in row order all the same.
     * Absent on a set written before frames carried rows: the editor derives
     * rows from `x`/`y` once, and packs frames that have neither.
     */
    row: z.number().int().nonnegative().optional(),
    col: z.number().int().nonnegative().optional(),
    /**
     * Top-left corner in world pixels as the editor laid the frame out from its
     * row. Written for readers that place things around frames without running
     * the layout, such as the agent putting quiz screens under the lowest frame.
     */
    x: z.number().optional(),
    y: z.number().optional(),
    format: canvasFormatSchema,
    safe: canvasSafeSchema.nullable(),
    /** The artboard fill: a frame field, not a layer, so it is never selectable. */
    background: canvasFillSchema.nullable(),
    layers: z.array(canvasLayerSchema),
    /** Set when Copy & resize derived this frame from another. */
    derivedFrom: canvasDerivedFromSchema.nullish(),
    /** Id of the template this frame was built from, for QA and "recreate". */
    templateId: z.string().nullish(),
    /** Execution metadata is separate from artwork and never exported. */
    generation: canvasFrameGenerationSchema.optional(),
  })
  .describe("CanvasFrame");

/* -------------------------------------------------------------------------- */
/* Items                                                                      */
/* -------------------------------------------------------------------------- */

/** The most items one set holds; a concept is tens of steps, not thousands. */
export const CANVAS_ITEMS_MAX = 200;

/** The most blocks one step holds; a screen is a handful of pieces. */
export const CANVAS_BLOCKS_MAX = 24;

/** The longest text one block holds; a line on a wireframe, not a page of copy. */
export const CANVAS_BLOCK_TEXT_MAX = 600;

const canvasBlockText = z.string().max(CANVAS_BLOCK_TEXT_MAX);

/** The longest HTML one step holds: a mockup of one screen, not a page. */
export const CANVAS_STEP_HTML_MAX = 20_000;

/** The longest label a step wears: a word or two of what it is for. */
export const CANVAS_STEP_LABEL_MAX = 80;

/** One piece of a screen. A kind exists only where the wireframe draws differently or an arrow can attach; everything else is a note. */
export const canvasBlockSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("text"),
      id: z.string().min(1),
      text: canvasBlockText,
    }),
    z.object({
      kind: z.literal("choice"),
      id: z.string().min(1),
      text: canvasBlockText,
    }),
    z.object({
      kind: z.literal("media"),
      id: z.string().min(1),
      /** What the picture or video is; the whole block when there is no `ref` yet. */
      text: canvasBlockText,
      ref: canvasImageRefSchema.optional(),
      /** Draws a play glyph; the file itself says what it really is. */
      video: z.boolean().optional(),
    }),
    z.object({
      kind: z.literal("button"),
      id: z.string().min(1),
      text: canvasBlockText,
      /** Where it sends the visitor, when it leaves the quiz. */
      url: z.string().max(2000).optional(),
    }),
    z.object({
      kind: z.literal("note"),
      id: z.string().min(1),
      /** An instruction to the agent. Never visitor copy. */
      text: canvasBlockText,
      /** Picks a glyph only (`loading`, `email`, `offer`…), so a new tray preset needs no schema change. */
      hint: z.string().max(40).optional(),
    }),
  ])
  .describe("CanvasBlock");

/** One screen of a quiz concept: a stack of blocks with no stored type, because what it is comes from what is on it. Never published or linked to a site file. */
export const canvasStepItemSchema = z
  .object({
    kind: z.literal("step"),
    id: z.string().min(1),
    /** Top-left corner in world pixels, the same space frames sit in. */
    x: z.number(),
    y: z.number(),
    /** Pieces from before screens were HTML; empty once `html` is set. */
    blocks: z.array(canvasBlockSchema).max(CANVAS_BLOCKS_MAX),
    /** The screen as an HTML fragment: its copy, structure and styling. Elements with a `data-id` can carry arrows. */
    html: z.string().max(CANVAS_STEP_HTML_MAX).optional(),
    /** What the screen is for, in the person's own words. Never a type. */
    label: z.string().max(CANVAS_STEP_LABEL_MAX).optional(),
    /** Choices are pick-any. They then carry no arrows of their own. */
    multi: z.boolean().optional(),
    /** `inspiration` is a look to follow, not a screen to build. Absent means a screen. */
    role: z.enum(["screen", "inspiration"]).optional(),
  })
  .describe("CanvasStepItem");

/** "The visitor goes from this step to that one", from the step or from one choice or button on it. */
export const canvasEdgeItemSchema = z
  .object({
    kind: z.literal("edge"),
    id: z.string().min(1),
    from: z.string().min(1),
    fromBlock: z.string().min(1).optional(),
    to: z.string().min(1),
    /** Free text for a rule about an earlier answer; absent means always. */
    condition: z.string().max(300).optional(),
  })
  .describe("CanvasEdgeItem");

/** A named group of frames and items. Its rectangle is derived from its members. */
export const canvasSectionItemSchema = z
  .object({
    kind: z.literal("section"),
    id: z.string().min(1),
    name: z.string().max(200),
    members: z.array(z.string().min(1)),
  })
  .describe("CanvasSectionItem");

export const canvasPageItemSchema = z
  .object({
    kind: z.literal("page"),
    id: z.string().min(1),
    x: z.number(),
    y: z.number(),
    shareId: z.uuid(),
    device: z.enum(["desktop", "tablet", "mobile"]),
    height: z
      .object({ value: z.number().finite().positive() })
      .optional()
      .describe(
        "Optional page preview body height in CSS pixels; omitted uses the device preset.",
      ),
    label: z.string().max(200).optional(),
  })
  .describe("CanvasPageItem");

export const canvasItemSchema = z
  .discriminatedUnion("kind", [
    canvasStepItemSchema,
    canvasEdgeItemSchema,
    canvasSectionItemSchema,
    canvasPageItemSchema,
  ])
  .describe("CanvasItem");

/* -------------------------------------------------------------------------- */
/* Sets                                                                       */
/* -------------------------------------------------------------------------- */

/** The highest on-disk version this build reads. A set is written as 1 unless it has items. */
export const CANVAS_SCHEMA_VERSION = 2;

/**
 * A whole `*.canvas.json` file. Frames are inline: images are handles rather
 * than bytes, so a set stays small and the Files app previews it from
 * `thumbnail` without a sidecar file.
 */
export const canvasSetSchema = z
  .object({
    schemaVersion: z.number().int().positive(),
    name: z.string(),
    /** Drive path of the brand draft the set was authored against, or null. Recorded by the agent; the editor does not read it. */
    brandPath: z.string().nullable(),
    frames: z.array(canvasFrameSchema),
    /** Everything that is not an artboard. Absent on an ad-only set, so those files do not change. */
    items: z.array(canvasItemSchema).max(CANVAS_ITEMS_MAX).optional(),
    /** Null uses the first six ads; a frame ID explicitly features that ad. */
    thumbnailFrameId: z.string().min(1).nullable().default(null),
    /** Derived set preview data URI, written on save. */
    thumbnail: z.string().nullable(),
  })
  .refine(
    (set) => {
      return (
        set.thumbnailFrameId === null ||
        set.frames.some((frame) => frame.id === set.thumbnailFrameId)
      );
    },
    {
      message: "Thumbnail frame must belong to the set",
      path: ["thumbnailFrameId"],
    },
  )
  .describe("CanvasSet");

/** The one place that decides the on-disk shape: no items means version 1 and no `items` key, so older builds keep opening it; with items it is version 2, which they refuse rather than strip. */
export function withCanvasSchemaVersion(
  set: z.infer<typeof canvasSetSchema>,
): z.infer<typeof canvasSetSchema> {
  const { items, ...rest } = set;
  return items?.length
    ? { ...rest, items, schemaVersion: 2 }
    : { ...rest, schemaVersion: 1 };
}

/** A blank set, as "New set" writes it before the first frame exists. */
export function emptyCanvasSet(name: string): z.infer<typeof canvasSetSchema> {
  return {
    schemaVersion: 1,
    name,
    brandPath: null,
    frames: [],
    thumbnailFrameId: null,
    thumbnail: null,
  };
}

export class CanvasSetUnreadableError extends ReploError {}

/** Parse a `*.canvas.json` body. An empty body is a set whose first write never landed. */
export function parseCanvasSetText({
  text,
  title,
}: {
  text: string;
  title: string;
}): z.infer<typeof canvasSetSchema> {
  if (!text.trim()) {
    return emptyCanvasSet(title);
  }
  const parsed = canvasSetSchema.safeParse(parseJsonOrNull(text));
  if (!parsed.success) {
    throw new CanvasSetUnreadableError({
      message: "This file has content Canvas can't read.",
    });
  }
  if (parsed.data.schemaVersion > CANVAS_SCHEMA_VERSION) {
    throw new CanvasSetUnreadableError({
      message: "This set was saved by a newer version of Canvas.",
    });
  }
  return parsed.data;
}
